<template>
  <div class="shipment-world-map">
    <div class="map-controls">
      <label class="toggle-label">
        <input type="checkbox" v-model="showBaseLines" />
        Sevkiyat çizgilerini göster
      </label>
    </div>
    <div ref="chart" class="echart-container" :style="{ height: computedHeight }"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import debounce from 'lodash.debounce';

export default {
  name: 'ShipmentWorldMap',
  props: {
    // [[ [lon, lat], [lon, lat] ], ...]
    routes: {
      type: Array,
      default: () => []
    },
    // Number (px) or CSS string value (e.g. '60vh')
    height: {
      type: [Number, String],
      default: 480
    },
    // Initial zoom level for the world map
    zoom: {
      type: [Number, String],
      default: 1.2
    },
    // Optional initial center [lon, lat]; if not provided, center is derived from routes
    center: {
      type: Array,
      default: () => [35, 39] // Türkiye merkezine yakın (lon, lat)
    },
    // Renderer: 'canvas' (çok veri için hızlı) veya 'svg' (az veri için keskin)
    renderer: {
      type: String,
      default: 'canvas'
    },
    // Aynı güzergâhları birleştir (toplulaştırma)
    enableAggregation: {
      type: Boolean,
      default: true
    },
    // Toplulaştırmadan sonra en fazla kaç çizgi gösterilsin
    maxRoutes: {
      type: Number,
      default: 300
    },
    // En az kaç adet olursa bir çizgi gösterilsin (toplulaştırmada)
    minRouteCount: {
      type: Number,
      default: 1
    },
    // Çizgi efektlerini (ok/ripple) göstermek için üst sınır (üstündeyse kapat)
    showEffectsThreshold: {
      type: Number,
      default: 50
    },
    // Kısa mesafe (şehir/içi/ülke içi) eşik km: bunun altı sadece yakın zoom'da gösterilir
    localRouteMaxKm: {
      type: Number,
      default: 1600
    },
    // Kısa mesafelerin görünmesi için gereken minimum zoom
    minZoomForLocalRoutes: {
      type: Number,
      default: 7
    },
    // Noktaların (destination) görüneceği maksimum zoom (harita küçükken noktaları göster)
    maxZoomForPoints: {
      type: Number,
      default: 3.5
    },
    // Her durumda (yüksek hacimde dahi) az sayıda çizgide animasyon göster
    animateTopNLines: {
      type: Number,
      default: 24
    },
    // Animasyonlu alt kümeyi şu aralıkla döndür (ms)
    animateRotateMs: {
      type: Number,
      default: 4000
    },
    // Animasyon ayarları
    effectPeriod: {
      type: Number,
      default: 6
    },
    effectSymbolSize: {
      type: Number,
      default: 6
    },
    effectTrailLength: {
      type: Number,
      default: 0.15
    }
  },
  data() {
    return {
      chart: null,
      isMapReady: false,
      updateChartDebounced: null,
      // Animasyon için dahili durum
      currentZoom: 1,
      lastZoom: 1,
      lastShowLocal: null,
      allLinesCoords: [],
      animatedRotateEnabled: false,
      animationTimer: null,
      animatedStartIndex: 0,
      reverseDirection: false,
      roamDebounceTimer: null,
      isRoaming: false,
      // UI: statik çizgileri göster/gizle
      showBaseLines: false
    };
  },
  computed: {
    computedHeight() {
      if (typeof this.height === 'number') return `${this.height}px`;
      return this.height || '480px';
    }
  },
  created() {
    this.currentZoom = Number(this.zoom) || 1;
    this.lastZoom = this.currentZoom;
    this.lastShowLocal = this.currentZoom >= Number(this.minZoomForLocalRoutes);
    this.updateChartDebounced = debounce(() => {
      this.updateChart();
    }, 150);
  },
  mounted() {
    this.initChart();
  },
  beforeDestroy() {
    if (this.chart) {
      if (this.chart.off) this.chart.off('georoam', this.onGeoRoam);
      this.chart.dispose();
      this.chart = null;
    }
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }
    window.removeEventListener('resize', this.resizeHandler);
  },
  methods: {
    async registerWorldMap() {
      const cdnList = [
        'https://fastly.jsdelivr.net/npm/echarts/map/json/world.json',
        'https://unpkg.com/echarts@5/map/json/world.json'
      ];
      for (let i = 0; i < cdnList.length; i += 1) {
        try {
          const res = await fetch(cdnList[i], { cache: 'force-cache' });
          if (!res.ok) throw new Error('response not ok');
          const worldJson = await res.json();
          echarts.registerMap('world', worldJson);
          this.isMapReady = true;
          return true;
        } catch (e) {
          // try next
        }
      }
      // eslint-disable-next-line no-console
      console.warn('[ShipmentWorldMap] world.json yüklenemedi, harita olmadan çizim yapılamaz.');
      this.isMapReady = false;
      return false;
    },
    async initChart() {
      if (!this.chart) {
        this.chart = echarts.init(this.$refs.chart, null, { renderer: this.renderer || 'canvas' });
      }

      await this.registerWorldMap();

      if (this.isMapReady) {
        // İlk kurulumda geo + arkaplanı tek seferde uygula
        const initialOption = this.buildOption();
        this.chart.setOption(
          {
            animation: false,
            animationDurationUpdate: 0,
            backgroundColor: initialOption.backgroundColor,
            geo: initialOption.geo
          },
          { notMerge: true }
        );
        // Ardından sadece serileri uygula (kısmi güncelleme)
        this.chart.setOption(
          { series: initialOption.series },
          { notMerge: false, replaceMerge: ['series'], lazyUpdate: true }
        );
      }
      this.resizeHandler = () => {
        if (this.chart) this.chart.resize();
      };
      window.addEventListener('resize', this.resizeHandler);
      // Zoom/pan değişimini dinle
      if (this.chart && this.chart.on) {
        this.chart.on('georoam', this.onGeoRoam);
      }
    },
    onGeoRoam() {
      try {
        const opt = this.chart?.getOption();
        const z = opt?.geo && Array.isArray(opt.geo) ? opt.geo[0]?.zoom : undefined;
        if (typeof z !== 'undefined') {
          const newZoom = Number(z) || this.currentZoom;
          // Sadece anlamlı zoom değişiminde ve görünürlük eşiği geçişinde güncelle
          const epsilon = 0.01;
          const prevShowLocal = this.lastShowLocal;
          const nextShowLocal = newZoom >= Number(this.minZoomForLocalRoutes);
          const zoomChanged = Math.abs(newZoom - this.lastZoom) > epsilon;
          this.currentZoom = newZoom;
          if (zoomChanged) {
            this.lastZoom = newZoom;
            if (prevShowLocal !== nextShowLocal) {
              this.lastShowLocal = nextShowLocal;
              if (this.updateChartDebounced) this.updateChartDebounced();
              else this.updateChart();
            }
          }
        }
      } catch (e) {
        // ignore
      }
      // zoom okunamadıysa, gereksiz yeniden çizim yapma
    },
    setOverlayEffectEnabled(enabled) {
      if (!this.chart) return;
      this.chart.setOption(
        {
          series: [
            {
              id: 'moving-overlay',
              effect: {
                show: !!enabled
              }
            }
          ]
        },
        { notMerge: false, replaceMerge: ['series'], lazyUpdate: true }
      );
    },
    haversineKm(a, b) {
      const toRad = d => (d * Math.PI) / 180;
      const R = 6371; // km
      const [lon1, lat1] = a;
      const [lon2, lat2] = b;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const s1 = Math.sin(dLat / 2) ** 2;
      const s2 = Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      const c = 2 * Math.asin(Math.sqrt(s1 + s2));
      return R * c;
    },
    buildOption() {
      const normalizeCoord = coord => [Number(coord[0]), Number(coord[1])];
      const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

      // Toplulaştırma: Aynı güzergâhları say
      let aggregated = [];
      if (this.enableAggregation) {
        const map = new Map();
        (this.routes || []).forEach(pair => {
          if (!Array.isArray(pair) || pair.length < 2) return;
          const a = normalizeCoord(pair[0]);
          const b = normalizeCoord(pair[1]);
          const key = `${a[0]},${a[1]}|${b[0]},${b[1]}`; // yön korunur
          const item = map.get(key) || { a, b, count: 0, km: this.haversineKm(a, b) };
          item.count += 1;
          map.set(key, item);
        });
        aggregated = Array.from(map.values())
          .filter(it => it.count >= this.minRouteCount)
          .sort((x, y) => y.count - x.count);
        if (this.maxRoutes > 0 && aggregated.length > this.maxRoutes) {
          aggregated = aggregated.slice(0, this.maxRoutes);
        }
      } else {
        aggregated = (this.routes || []).map(pair => {
          const a = normalizeCoord(pair[0]);
          const b = normalizeCoord(pair[1]);
          return { a, b, count: 1, km: this.haversineKm(a, b) };
        });
      }

      // Zoom tabanlı görünürlük: kısa mesafeler yalnızca yakın zoom'da
      const visibleAggregated = aggregated.filter(item => {
        const isLocal = item.km < this.localRouteMaxKm;
        if (!isLocal) return true; // uzun mesafeler her zaman görünür
        return this.currentZoom >= Number(this.minZoomForLocalRoutes) || false;
      });

      const totalLines = visibleAggregated.length;
      const showEffectsForAll = totalLines <= this.showEffectsThreshold;

      // Kalınlık ve saydamlığı yoğunluğa göre ölçekle
      const computeWidth = count => clamp(1 + Math.log2(count + 1), 1, 8);

      const linesDataAll = visibleAggregated.map(item => ({
        coords: [item.a, item.b],
        lineStyle: {
          width: computeWidth(item.count),
          opacity: clamp(0.3 + Math.min(item.count / 50, 0.5), 0.3, 0.8)
        }
      }));

      // Tam koordinat havuzunu sakla (animasyon alt kümesi için)
      this.allLinesCoords = visibleAggregated.map(item => [item.a, item.b]);
      // Alt küme rotasyonunu kapat: tüm hatlarda kesintisiz animasyon
      this.animatedRotateEnabled = false;
      // Hareketli seride item-level lineStyle kullanma; yalnızca oklar görünsün
      const linesDataAnimated = this.allLinesCoords.map(coords => ({ coords }));

      // Unique points for scatter
      const pointsSet = new Set();
      const pointsData = [];
      // Sadece VARIŞ (destination) noktalarını göster
      visibleAggregated.forEach(({ b }) => {
        const key = `${b[0]},${b[1]}`;
        if (!pointsSet.has(key)) {
          pointsSet.add(key);
          pointsData.push({ value: b });
        }
      });

      // Determine initial center
      let initialCenter;
      if (Array.isArray(this.center) && this.center.length === 2) {
        initialCenter = normalizeCoord(this.center);
      } else if (pointsData.length > 0) {
        const sum = pointsData.reduce(
          (acc, p) => {
            acc[0] += Number(p.value[0]);
            acc[1] += Number(p.value[1]);
            return acc;
          },
          [0, 0]
        );
        initialCenter = [sum[0] / pointsData.length, sum[1] / pointsData.length];
      }

      const initialZoom = Number(this.zoom) || 1;

      // Statik taban çizgi serisi (UI ile aç/kapa)
      // Fazla hat olduğunda rengi daha saydam yap
      let baseLineAlpha = 0.28;
      if (totalLines > 500) baseLineAlpha = 0.08;
      else if (totalLines > 200) baseLineAlpha = 0.12;
      else if (totalLines > 100) baseLineAlpha = 0.18;
      const baseLinesSeries = {
        id: 'base-lines',
        name: 'All Shipment Lines',
        type: 'lines',
        coordinateSystem: 'geo',
        zlevel: 2,
        effect: undefined,
        lineStyle: {
          color: `rgba(166, 200, 76, ${baseLineAlpha})`,
          opacity: 0.35,
          width: 1.25,
          curveness: 0.2
        },
        silent: true,
        progressive: 2000,
        progressiveThreshold: 5000,
        data: this.showBaseLines ? linesDataAll : []
      };

      return {
        backgroundColor: '#404a59',
        geo: {
          map: 'world',
          roam: true,
          zoom: initialZoom,
          center: initialCenter,
          scaleLimit: { min: 0.5, max: 20 },
          label: { show: false }, // normalde kapalı
          emphasis: {
            label: {
              show: true
            }
          },
          itemStyle: {
            areaColor: '#323c48',
            borderColor: '#111'
          },
          silent: false
        },
        series: [
          baseLinesSeries,
          {
            id: 'moving-overlay',
            name: 'Moving Arrows',
            type: 'lines',
            coordinateSystem: 'geo',
            zlevel: 3,
            effect: {
              // Checkbox açık/kapalı fark etmeksizin oklar görünsün
              show: true,
              period: this.effectPeriod,
              // Checkbox kapalıyken sadece oklar görünsün diye iz bırakmayı kapat
              // Checkbox açıkken sabit çizgiler üzerinde kısa bir iz bıraksın
              trailLength: this.showBaseLines ? Math.max(0.1, this.effectTrailLength) : 0.25,
              // Kapalıyken daha saydam bir renk ile kuyruklu yıldız efekti
              color: this.showBaseLines ? '#a6c84c' : 'rgba(166, 200, 76, 0.7)',
              symbol: 'arrow',
              symbolSize: 5
            },

            lineStyle: {
              // Arkadaki yol çizgisini tamamen görünmez yap
              opacity: 0,
              curveness: 0.2
            },
            progressive: 2000,
            progressiveThreshold: 5000,
            silent: true,
            data: linesDataAnimated
          },
          {
            type: showEffectsForAll ? 'effectScatter' : 'scatter',
            coordinateSystem: 'geo',
            zlevel: 3,
            rippleEffect: showEffectsForAll ? { brushType: 'stroke' } : undefined,
            label: { show: false },
            symbolSize: showEffectsForAll ? 6 : 4,
            itemStyle: { color: '#a6c84c' },
            large: true,
            largeThreshold: 2000,
            progressive: 5000,
            progressiveThreshold: 10000,
            silent: true,
            // Harita küçükken (zoom düşük), noktalar görünsün; yakın zoom'da gizle
            data: this.currentZoom <= Number(this.maxZoomForPoints) ? pointsData : []
          }
        ]
      };
    },
    setupAnimationTimer() {
      if (this.animationTimer) {
        clearInterval(this.animationTimer);
        this.animationTimer = null;
      }
      if (!this.animatedRotateEnabled) return;
      const interval = Math.max(1000, Number(this.animateRotateMs) || 4000);
      this.animationTimer = setInterval(() => {
        if (!this.chart || !this.allLinesCoords || this.allLinesCoords.length === 0) return;
        this.animatedStartIndex =
          (this.animatedStartIndex + this.animateTopNLines) % this.allLinesCoords.length;
        const n = Math.min(this.animateTopNLines, this.allLinesCoords.length);
        const start = this.animatedStartIndex;
        const end = start + n;
        let subset;
        if (end <= this.allLinesCoords.length) {
          subset = this.allLinesCoords.slice(start, end);
        } else {
          subset = [
            ...this.allLinesCoords.slice(start),
            ...this.allLinesCoords.slice(0, end - this.allLinesCoords.length)
          ];
        }
        const linesDataAnimated = subset.map(coords => ({ coords }));
        // Sadece overlay serisini güncelle
        this.chart.setOption(
          {
            series: [
              { id: 'moving-overlay', data: linesDataAnimated } // hedefe yönelik güncelleme
            ]
          },
          { notMerge: false, replaceMerge: ['series'], lazyUpdate: true }
        );
      }, interval);
    },
    updateChart() {
      if (!this.chart || !this.isMapReady) return;
      if (!this.routes || this.routes.length === 0) {
        // eslint-disable-next-line no-console
        console.warn('[ShipmentWorldMap] routes boş, çizilecek hat bulunamadı.');
      }
      const option = this.buildOption();
      // Sadece serileri kısmi olarak güncelle
      this.chart.setOption(
        { series: option.series },
        { notMerge: false, replaceMerge: ['series'], lazyUpdate: true }
      );
      // Animasyon zamanlayıcısını güncelle
      this.setupAnimationTimer();
    }
  },
  watch: {
    routes: {
      handler() {
        // Harita hazır olmadan çizim yapma
        if (!this.isMapReady) return;
        this.$nextTick(() => {
          if (this.updateChartDebounced) this.updateChartDebounced();
          else this.updateChart();
        });
      },
      deep: true
    },
    showBaseLines() {
      this.$nextTick(() => {
        this.updateChart();
      });
    },
    height() {
      // kapsayıcı yüksekliği değişince yeniden boyutlandır
      this.$nextTick(() => {
        if (this.chart) this.chart.resize();
      });
    },
    zoom() {
      this.$nextTick(() => {
        this.updateChart();
      });
    },
    center: {
      handler() {
        this.$nextTick(() => {
          this.updateChart();
        });
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.shipment-world-map {
  width: 100%;
  position: relative;
}
.echart-container {
  width: 100% !important;
  height: 100% !important;
}
.map-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  user-select: none;
}
.toggle-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}
</style>
